/**
 * 起始身份数据
 * 修仙之路的起点，随机分配，部分可后期改变
 */

import type { RootGrade } from './spiritualRoots';
import type { Origin } from './origins';

export type SectName = 'sword' | 'alchemy' | 'array' | 'demon';

export interface Identity {
  id: string;
  name: string;
  desc: string;
  /** 所属宗门（仅门派弟子有） */
  sect?: SectName;
  sectName?: string;
  startingStones: number;
  startingItems: string[];
  startingEquipment?: string[];
  /** 永久效果 */
  permAttackBonus?: number;
  permDefBonus?: number;
  permExpBonus?: number;     // 修炼速度加成
  permBreakthroughBonus?: number;
  combatStoneBonus?: number; // 战斗灵石加成
  pillEffectBonus?: number;  // 丹药效果加成
  shopPillDiscount?: number;
  /** 初始debuff */
  startingHpPercent?: number;
  startingMpPercent?: number;
  /** 逃脱后获得的buff（仅被囚身份） */
  escapeBuff?: {
    expBonus: number;
    breakthroughCount: number;  // 持续多少次突破
  };
  changeable: boolean;
  /** 是否有初始剧情 */
  hasOpeningScene: boolean;
}

export const IDENTITIES: Identity[] = [
  // ===== 散修 =====
  {
    id: 'rogue',
    name: '散修',
    desc: '无门无派，无依无靠。修仙界最庞大的群体，也是最艰难的群体。一切靠自己，自由的代价是孤独。',
    startingStones: 30,
    startingItems: ['hp_pill'],
    changeable: true,
    hasOpeningScene: false,
  },

  // ===== 门派弟子 — 剑宗 =====
  {
    id: 'sect_sword',
    name: '门派弟子·剑宗',
    desc: '剑宗主杀伐，以攻击见长。门下弟子个个是好战分子，战斗力在同阶中首屈一指。',
    sect: 'sword',
    sectName: '剑宗',
    startingStones: 80,
    startingItems: ['hp_pill', 'hp_pill', 'hp_pill', 'mp_pill', 'mp_pill'],
    permAttackBonus: 0.08,
    combatStoneBonus: 0.25,
    changeable: true,
    hasOpeningScene: false,
  },

  // ===== 门派弟子 — 丹宗 =====
  {
    id: 'sect_alchemy',
    name: '门派弟子·丹宗',
    desc: '丹宗以炼丹术闻名，门下弟子丹药不缺，也更懂得如何最大化药效。',
    sect: 'alchemy',
    sectName: '丹宗',
    startingStones: 80,
    startingItems: ['hp_pill', 'hp_pill', 'hp_pill', 'mp_pill', 'mp_pill'],
    pillEffectBonus: 0.25,
    shopPillDiscount: 0.2,
    changeable: true,
    hasOpeningScene: false,
  },

  // ===== 门派弟子 — 阵宗 =====
  {
    id: 'sect_array',
    name: '门派弟子·阵宗',
    desc: '阵宗精研阵法，以静制动。修炼环境远优于其他宗门，突破时也得阵法加持。',
    sect: 'array',
    sectName: '阵宗',
    startingStones: 80,
    startingItems: ['hp_pill', 'hp_pill', 'hp_pill', 'mp_pill', 'mp_pill'],
    permExpBonus: 0.08,
    permBreakthroughBonus: 0.05,
    changeable: true,
    hasOpeningScene: false,
  },

  // ===== 门派弟子 — 魔宗 =====
  {
    id: 'sect_demon',
    name: '门派弟子·魔宗',
    desc: '魔宗不守正道规矩，行事狠辣。战斗收益远超其他宗门，但正道修士视你为敌。',
    sect: 'demon',
    sectName: '魔宗',
    startingStones: 80,
    startingItems: ['hp_pill', 'hp_pill', 'hp_pill', 'mp_pill', 'mp_pill'],
    combatStoneBonus: 0.4,
    changeable: true,
    hasOpeningScene: false,
  },

  // ===== 前辈收徒 =====
  {
    id: 'master_disciple',
    name: '前辈收徒',
    desc: '一位云游四海的元婴期老怪偶然路过，在你的灵根中看到了罕见的潜力。他决定收你为关门弟子。但师父的仇家遍布修仙界，你的路不会太平。',
    startingStones: 300,
    startingItems: ['exp_pill', 'exp_pill', 'exp_pill', 'exp_pill', 'exp_pill'],
    startingEquipment: ['spirit_sword'],
    permExpBonus: 0.12,
    changeable: false,
    hasOpeningScene: false,
  },

  // ===== 被囚/炉鼎 =====
  {
    id: 'imprisoned',
    name: '被囚/炉鼎',
    desc: '天资太好也是一种诅咒。一名结丹期的邪修在云游时发现了你，将你囚禁起来，打算在下次血月之夜将你的灵根炼化为己用。你必须在血月降临之前逃出去。',
    startingStones: 0,
    startingItems: [],
    startingHpPercent: 0.5,
    startingMpPercent: 0,
    escapeBuff: {
      expBonus: 0.20,
      breakthroughCount: 20,
    },
    changeable: false,
    hasOpeningScene: true,
  },

  // ===== 被遣散 =====
  {
    id: 'expelled',
    name: '被遣散',
    desc: '曾加入过一个小门派，因灵根资质太差被无情遣散。身上只剩一枚被视为废品的丹药和一腔不甘。但谁说废灵根不能翻身？韩立当年也是伪灵根。',
    startingStones: 10,
    startingItems: ['hp_pill'],
    permExpBonus: -0.05,     // 修炼速度略慢（废灵根）
    changeable: false,
    hasOpeningScene: false,
  },
];

/**
 * 根据灵根品质和出身，加权随机分配身份
 */
export function assignIdentity(grade: RootGrade, origin: Origin): Identity {
  const isRich = origin.id === 'cultivation_family';
  const isOrphan = origin.id === 'wandering_orphan';

  const pool: { identity: Identity; weight: number }[] = [];

  // 散修：所有人都可能
  let rogueWeight = 60;
  if (grade === '天') rogueWeight = 30; // 天灵根容易被人盯上
  if (grade === '地') rogueWeight = 50;
  pool.push({ identity: IDENTITIES[0], weight: rogueWeight });

  // 门派弟子：玄及以上，非孤儿
  if (grade !== '黄' && !isOrphan) {
    let sectWeight = 25;
    if (isRich) sectWeight += 10;
    // 随机分配一个宗门
    const sectIdx = 1 + Math.floor(Math.random() * 4); // 1-4: sword/alchemy/array/demon
    pool.push({ identity: IDENTITIES[sectIdx], weight: sectWeight });
  }

  // 前辈收徒：天灵根，或地灵根+修仙家族
  if (grade === '天') {
    pool.push({ identity: IDENTITIES[5], weight: 5 });
  } else if (grade === '地' && isRich) {
    pool.push({ identity: IDENTITIES[5], weight: 3 });
  }

  // 被囚：天灵根，或地灵根+非世家（没有家族保护）
  if (grade === '天') {
    pool.push({ identity: IDENTITIES[6], weight: 10 });
  } else if (grade === '地' && !isRich) {
    pool.push({ identity: IDENTITIES[6], weight: 5 });
  }

  // 被遣散：黄灵根 + 非世家
  if (grade === '黄' && !isRich) {
    pool.push({ identity: IDENTITIES[7], weight: 15 });
  }

  // 加权随机
  const totalWeight = pool.reduce((sum, p) => sum + p.weight, 0);
  let roll = Math.random() * totalWeight;
  for (const entry of pool) {
    roll -= entry.weight;
    if (roll <= 0) return entry.identity;
  }

  return IDENTITIES[0]; // fallback: 散修
}
